import { useState, useEffect, useCallback } from "react";
import { getSumSubAccessToken } from "../api/api";
import { getUserIpAddress } from "../utils/getUserIpAddress";

// Define types for Sumsub WebSDK messages
export interface SumsubMessage {
  type: string;
  payload?: {
    applicantId?: string;
    reviewStatus?: string;
    [key: string]: unknown;
  };
}

interface UseSumSubOptions {
  eoaAddress?: string;
  onApplicantIdChange?: (id: string) => void;
  onVerificationComplete?: () => void;
}

export const useSumSub = ({
  eoaAddress,
  onApplicantIdChange,
  onVerificationComplete,
}: UseSumSubOptions) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tokenRefreshCount, setTokenRefreshCount] = useState(0);
  const [applicantId, setApplicantId] = useState<string | null>(null);

  // Function to get a new access token from our backend server
  const getAccessToken = useCallback(async () => {
    console.log("Attempting to get SumSub access token...");
    setIsLoading(true);
    setError(null);
    try {
      if (!eoaAddress) throw new Error("EOA address not provided");

      // Get user's IP address
      const ipAddress = await getUserIpAddress();

      const tokenData = await getSumSubAccessToken(eoaAddress, ipAddress);

      if (!tokenData?.token) {
        throw new Error("No token received from backend");
      }

      setAccessToken(tokenData.token);
      setTokenRefreshCount(0);
      console.log("SumSub access token obtained successfully.");
    } catch (err) {
      console.error("Error getting access token:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to get access token. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  }, [eoaAddress]);

  useEffect(() => {
    if (eoaAddress) {
      getAccessToken();
    }
  }, [getAccessToken, eoaAddress]);

  // Handle WebSDK messages
  const handleMessage = useCallback(
    (message: SumsubMessage) => {
      console.log("Sumsub Message Received:", message.type, message.payload);

      // Extract applicantId if available
      if (
        message.payload?.applicantId &&
        typeof message.payload.applicantId === "string"
      ) {
        const newApplicantId = message.payload.applicantId;
        if (newApplicantId !== applicantId) {
          setApplicantId(newApplicantId);
          onApplicantIdChange?.(newApplicantId);
          console.log("Applicant ID updated:", newApplicantId);
        }
      }

      // Check for verification completion
      if (
        message.type === "idCheck.applicantReviewed" ||
        message.payload?.reviewStatus === "completed"
      ) {
        console.log("Verification process completed.");
        onVerificationComplete?.();
      }
    },
    [applicantId, onApplicantIdChange, onVerificationComplete]
  );

  // Handle WebSDK errors
  const handleError = useCallback((sdkError: Error) => {
    console.error("Sumsub WebSDK error:", sdkError);
    setError(`Sumsub SDK Error: ${sdkError.message}. Please try again.`);
  }, []);

  // Handle token expiration
  const handleTokenExpiration = useCallback(async () => {
    console.warn("Sumsub token expired, attempting refresh...");

    if (tokenRefreshCount >= 2) {
      console.error("Max token refresh attempts reached.");
      setError("Failed to refresh session. Please try again later.");
      setAccessToken(null);
      return;
    }

    setTokenRefreshCount((prev) => prev + 1);
    setTimeout(async () => {
      await getAccessToken();
    }, 2000);
  }, [tokenRefreshCount, getAccessToken]);

  const retry = useCallback(() => {
    setTokenRefreshCount(0);
    getAccessToken();
  }, [getAccessToken]);

  return {
    accessToken,
    isLoading,
    error,
    applicantId,
    handleMessage,
    handleError,
    handleTokenExpiration,
    retry,
  };
};
